import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { Modal, Button } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { notifySuccess } from '@rsos/utils/toastUtils';
import { updateIntegrationCredential } from '../../actions';
import {
  GENERIC_REQUEST_ERROR_MESSAGE_PRIMARY,
  GENERIC_REQUEST_ERROR_MESSAGE_SECONDARY,
  CREDENTIAL_ERROR_MESSAGE,
} from '../../constants/errorMessages';
import { defaultSchema } from '../partnerSchema';
import { validationSchema } from '../validationSchema';
import { ButtonContainer } from './Modal.styles';
import DataSourcesForm from './components/DataSourcesForm';
import { ErrorContainer } from './components/ErrorContainer';
import { ModalHeaders } from './components/ModalHeaders';
import { parsedHttp } from './parsedHttp';

const EditCredentialModal = ({
  orgID,
  onClose,
  isOpened,
  selectedEditCredential,
}) => {
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const initialValues = Object.keys(defaultSchema).reduce((a, c) => {
    a[c] =
      selectedEditCredential && selectedEditCredential[c]
        ? selectedEditCredential[c]
        : '';
    return a;
  }, {});

  const checkBtnDisabled = values => {
    if (values) {
      let hasInvalid = false;
      for (const [k, v] of Object.entries(values)) {
        if (v.trim() === '' && k !== 'device_search_subdomain') {
          hasInvalid = true;
        }
      }
      return hasInvalid;
    }
  };

  const handleSubmit = async values => {
    const device_search_subdomain = values.device_search_subdomain
      ? `${parsedHttp(values.device_search_subdomain)}`
      : '';

    const normalizedCredential = {
      ...selectedEditCredential,
      ...values,
      device_search_subdomain,
    };

    // should remove device_search_subdomain key when it is empty:
    if (!device_search_subdomain) {
      delete normalizedCredential['device_search_subdomain'];
    }

    const response = await dispatch(
      updateIntegrationCredential(orgID, normalizedCredential),
    );
    if (response.id) {
      onClose();
      notifySuccess('Credentials have been validated.');
      setError(null);
    } else {
      setError(response.status);
    }
  };

  const handleChangeWithError = (onChange, e) => {
    if (error) {
      setError(null);
    }
    onChange(e);
  };

  return (
    <Modal
      id="edit-user-modal"
      isOpened={isOpened}
      onClose={onClose}
      width={510}
      disableBackdropClick
    >
      <ModalHeaders
        selected
        handleCloseModal={onClose}
        focusedData={selectedEditCredential}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ values, touched, handleBlur, handleChange, setFieldValue }) => (
          <>
            <ModalBody>
              <DataSourcesForm
                values={values}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                defaultSchema={defaultSchema}
                handleChangeWithError={handleChangeWithError}
              />
            </ModalBody>

            {error && (
              <ErrorContainer
                primaryText={
                  error === 400
                    ? CREDENTIAL_ERROR_MESSAGE
                    : GENERIC_REQUEST_ERROR_MESSAGE_PRIMARY
                }
                secondaryText={
                  error === 400 ? null : GENERIC_REQUEST_ERROR_MESSAGE_SECONDARY
                }
              />
            )}

            <ModalFooter justify="flex-end">
              <ButtonContainer horizontal>
                <Button btnType="tertiary" onClick={onClose} size="large">
                  Cancel
                </Button>
                <Button
                  size="large"
                  btnType="primary"
                  mr={0}
                  onClick={() => handleSubmit(values)}
                  disabled={checkBtnDisabled(values)}
                >
                  Submit
                </Button>
              </ButtonContainer>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default EditCredentialModal;
